// Chakra imports
import { Box, Flex, Grid, GridItem, Text, useColorModeValue } from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { FiArrowUpRight, FiCreditCard, FiMoreHorizontal, FiRefreshCw, FiShoppingBag } from 'react-icons/fi';
// Custom components
import Card from 'components/card/Card';
import { maskCurrency } from 'utils/number';
import { AggregateSection, splitOriginKey } from 'utils/balanceOrigin';
import { getVariable } from 'whitelabel';

// Each origin keeps one accent glyph; color stays a single brand accent so the
// principal figures — not the chrome — carry the hierarchy.
const ORIGIN_ICON: Record<string, IconType> = {
  SELL: FiShoppingBag,
  RECHARGE: FiRefreshCw,
  CARD_RECHARGE: FiCreditCard,
  WITHDRAW: FiArrowUpRight,
};

// SYSTEM_FEE and FEE both label as "Taxa"; brand-tag the system fee so the two
// rows are no longer indistinguishable (mirrors TypeRender in balances.tsx).
function resolveLabel(key: string, fallback: string): string {
  const { base } = splitOriginKey(key);
  if (base === 'SYSTEM_FEE') return `Taxa ${getVariable('name')}`;
  return fallback;
}

type Item = AggregateSection['items'][number];

function OriginCard({ section }: { section: AggregateSection }) {
  const ink = useColorModeValue('secondaryGray.900', 'white');
  const muted = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
  const eyebrow = useColorModeValue('secondaryGray.700', 'secondaryGray.400');
  const iconBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const hairline = useColorModeValue('secondaryGray.200', 'whiteAlpha.200');
  const track = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');

  const Icon = (section.origin && ORIGIN_ICON[section.origin]) || FiMoreHorizontal;

  // The first item is the origin's principal type (Venda / Depósito / Saque);
  // the rest are its components (fees, splits, commission...).
  const [principal, ...components] = section.items;
  // Bars compare the deductions to each other (largest deduction = full), and
  // only appear when there are 2+ to compare — a lone fee is just a row, never
  // a misleading full-width bar.
  const reference = components.length ? Math.max(...components.map((i: Item) => Math.abs(i.total))) : 0;
  const showBars = components.length >= 2;

  return (
    <Card px="24px" py="22px" border="1px solid" borderColor={hairline} boxShadow="sm">
      <Flex align="center" mb="18px">
        <Flex
          align="center"
          justify="center"
          boxSize="38px"
          borderRadius="12px"
          bg={iconBg}
          color="brand.500"
          flexShrink={0}
        >
          <Icon size={18} />
        </Flex>
        <Text
          ml="12px"
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.12em"
          textTransform="uppercase"
          color={eyebrow}
        >
          {section.label}
        </Text>
      </Flex>

      <Text fontSize="13px" fontWeight="500" color={muted} mb="2px">
        {resolveLabel(principal.key, principal.label)}
      </Text>
      <Text
        fontSize={{ base: '27px', md: '30px' }}
        fontWeight="700"
        lineHeight="1.05"
        letterSpacing="-0.02em"
        color={ink}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {maskCurrency(Math.abs(principal.total))}
      </Text>

      {components.length > 0 && (
        <Box mt="20px" pt="4px" borderTop="1px solid" borderColor={hairline}>
          {components.map((item: Item) => {
            const pct = reference > 0 ? (Math.abs(item.total) / reference) * 100 : 0;
            return (
              <Box
                key={item.key}
                pt="14px"
                pb="13px"
                borderBottom="1px solid"
                borderColor={hairline}
                _last={{ borderBottom: 'none', pb: '2px' }}
              >
                <Flex align="baseline" justify="space-between" gap="12px">
                  <Text fontSize="13.5px" fontWeight="500" color={muted} noOfLines={1}>
                    {resolveLabel(item.key, item.label)}
                  </Text>
                  <Text
                    fontSize="14px"
                    fontWeight="600"
                    color={ink}
                    flexShrink={0}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {maskCurrency(Math.abs(item.total))}
                  </Text>
                </Flex>
                {showBars && (
                  <Box mt="9px" h="3px" borderRadius="full" bg={track} overflow="hidden" aria-hidden>
                    <Box
                      h="100%"
                      borderRadius="full"
                      bg="brand.500"
                      opacity={0.7}
                      width={`${Math.max(pct, 2)}%`}
                      transition="width 0.5s ease"
                      sx={{
                        '@media (prefers-reduced-motion: reduce)': {
                          transition: 'none',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Card>
  );
}

export default function BalanceTotalsBoard({ sections }: { sections: AggregateSection[] }) {
  if (!sections.length) return null;

  return (
    <Grid
      templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
      gap={{ base: 4, md: 5 }}
      alignItems="start"
      px={2}
      py={5}
    >
      {sections.map((section) => (
        <GridItem key={section.label}>
          <OriginCard section={section} />
        </GridItem>
      ))}
    </Grid>
  );
}
